import React from 'react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box, { BoxProps } from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';

type Option = {
  id: string | number;
  name: string;
};

type PopoverPlacement = 'bottom' | 'top' | 'left' | 'right';

type MultipleSitesSelectionProps<T extends Option> = {
  value: T['id'][];
  options: T[];
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  label: string;
  open: boolean;
  onToggle: () => void;
  onChange: (ids: T['id'][]) => void;
  placeholder: string;
  placement?: PopoverPlacement;

  containerSx?: BoxProps['sx'];
  inputSx?: BoxProps['sx'];
  popoverSx?: BoxProps['sx'];
  listSx?: BoxProps['sx'];
};

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  '&.MuiCheckbox-root': {
    width: '45px',
    height: '45px',
  },
  '&.Mui-checked': {
    color: theme.palette.secondary.main,
  },
  '&.MuiCheckbox-indeterminate': {
    color: theme.palette.secondary.main,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 30,
  },
}));

const getPopoverPosition = (placement: PopoverPlacement) => {
  switch (placement) {
    case 'top':
      return {
        anchorOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
        transformOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
      };

    case 'left':
      return {
        anchorOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
        transformOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
      };

    case 'right':
      return {
        anchorOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
        transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
      };

    case 'bottom':
    default:
      return {
        anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
        transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
      };
  }
};

export default function MultipleSitesSelection<T extends Option>({
  value,
  options,
  error = false,
  helperText,
  disabled = false,
  label,
  open,
  onToggle,
  onChange,
  placeholder,
  placement = 'bottom',
  containerSx,
  inputSx,
  popoverSx,
  listSx,
}: MultipleSitesSelectionProps<T>) {
  const theme = useTheme();
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const labelId = '1';

  const selectedText = React.useMemo(() => {
    return options
      .filter((item) => value.includes(item.id))
      .map((item) => item.name)
      .join(', ');
  }, [options, value]);

  const handleSelectAll = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onChange(checked ? options.map((item) => item.id) : []);
  };

  const handleCheck = (id: T['id']) => {
    onChange(value.includes(id) ? value.filter((item) => item !== id) : [...value, id]);
  };

  const popoverPosition = getPopoverPosition(placement);

  return (
    <FormControl fullWidth variant="outlined" error={error} disabled={disabled} sx={containerSx}>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>

      <Box ref={anchorRef}>
        <OutlinedInput
          readOnly
          notched
          label={label}
          value={selectedText || ''}
          placeholder={placeholder}
          disabled={disabled}
          onClick={disabled ? undefined : onToggle}
          endAdornment={
            <InputAdornment position="end">{open ? <ExpandLess /> : <ExpandMore />}</InputAdornment>
          }
          sx={{
            width: '100%',
            cursor: disabled ? 'default' : 'pointer',

            '& input': {
              cursor: disabled ? 'default' : 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? theme.xPalette.warning : theme.xPalette.sliver,
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? theme.xPalette.warning : theme.xPalette.sliver,
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? theme.xPalette.warning : theme.palette.primary.main,
            },

            ...(inputSx as object),
          }}
        />
      </Box>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={onToggle}
        anchorOrigin={popoverPosition.anchorOrigin}
        transformOrigin={popoverPosition.transformOrigin}
        PaperProps={{
          sx: {
            width: anchorRef.current?.offsetWidth || 360,
            maxWidth: anchorRef.current?.offsetWidth || 360,
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: '10px',
            border: `1px solid ${error ? theme.xPalette.warning : theme.xPalette.sliver}`,
            boxShadow: 'none',
            boxSizing: 'border-box',
            ...(popoverSx as object),
          },
        }}
      >
        <List
          component="div"
          sx={{
            p: '12px 16px',
            m: 0,
            width: '100%',
            boxSizing: 'border-box',
            ...(listSx as object),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 44 }}>
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={options.length > 0 && value.length === options.length}
                  indeterminate={value.length > 0 && value.length < options.length}
                  onChange={handleSelectAll}
                />
              }
              label="Select All Site"
            />
          </Box>

          {options.map((site) => (
            <FormControlLabel
              key={site.id}
              control={
                <StyledCheckbox
                  checked={value.includes(site.id)}
                  onChange={() => handleCheck(site.id)}
                />
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 44,
                pl: '24px',
              }}
              label={
                <Typography
                  title={site.name}
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    fontSize: 16,
                    color: theme.palette.text.primary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {site.name}
                </Typography>
              }
            />
          ))}
        </List>
      </Popover>
    </FormControl>
  );
}
